import { useState } from 'react'
import { FileText } from 'lucide-react'
import { CHAT_COPY, useAttachmentUrls, type ChatAttachment } from '@/entities/chat'
import { isImageMime } from './chatHelpers'
import { AttachmentLightbox } from './AttachmentLightbox'

export function AttachmentTiles({
  conversationId,
  attachments,
}: {
  conversationId: number
  attachments: ChatAttachment[]
}) {
  const [failed, setFailed] = useState<Record<string, boolean>>({})
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const { data: urls, refetch } = useAttachmentUrls(
    conversationId,
    attachments.map((attachment) => attachment.fileId),
  )

  if (attachments.length === 0) return null

  const images = attachments.filter((attachment) => isImageMime(attachment.contentType))
  const files = attachments.filter((attachment) => !isImageMime(attachment.contentType))
  const visible = images.slice(0, 3)
  const overflow = images.length - visible.length

  const lightboxItems = images
    .map((image) => ({ fileId: image.fileId, name: image.name, url: urls?.[image.fileId] ?? '' }))
    .filter((item) => item.url !== '')

  return (
    <div className="mt-1.5 space-y-1.5">
      {visible.length > 0 && (
        <div className="flex gap-1.5">
          {visible.map((image, position) => {
            const url = urls?.[image.fileId]
            if (!url || failed[image.fileId]) {
              return (
                <button
                  key={image.fileId}
                  type="button"
                  onClick={() => {
                    setFailed((current) => ({ ...current, [image.fileId]: false }))
                    void refetch()
                  }}
                  className="h-20 w-20 rounded-md border border-line bg-surface-mute text-[11px] text-ink-3"
                >
                  {CHAT_COPY.attachmentFailed}
                </button>
              )
            }
            return (
              <button
                key={image.fileId}
                type="button"
                onClick={() => setLightboxIndex(position)}
                className="relative h-20 w-20 overflow-hidden rounded-md border border-line"
              >
                <img
                  src={url}
                  alt={image.name}
                  className="h-full w-full object-cover"
                  onError={() => setFailed((current) => ({ ...current, [image.fileId]: true }))}
                />
                {position === visible.length - 1 && overflow > 0 && (
                  <span className="absolute inset-0 flex items-center justify-center bg-ink/60 text-[13px] font-semibold text-surface">
                    +{overflow}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
      {files.map((file) => {
        const url = urls?.[file.fileId]
        return (
          <a
            key={file.fileId}
            href={url ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-md border border-line bg-surface px-2 py-1.5 text-[12.5px] text-ink-2 hover:bg-hover"
          >
            <FileText size={14} className="text-ink-4" />
            <span className="truncate">{file.name}</span>
          </a>
        )
      })}
      <AttachmentLightbox
        items={lightboxItems}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  )
}
